import DisplayFormatSelector, { DisplayFormat } from "components/charts/modern/inputs/DisplayFormatSelector";
import PlotTypeSelector from "components/charts/modern/inputs/PlotTypeSelector";
import OnlyPremiumRender from "components/conversion/OnlyPremiumRender";
import SadFaceIcon from "components/icons/SadFaceIcon";
import OperatingSegmentsList from "components/stocks/modern/OperatingSegments/OperatingSegmentsList";
import SingleValueSelect from "components/stocks/modern/OperatingSegments/SingleValueSelect";
import calculateFilterOptions from "components/stocks/modern/OperatingSegments/calculateFilterOptions";
import type { FinancialSegmentsIndicatorName } from "components/stocks/modern/OperatingSegments/genIndicatorsByNameMap";
import genIndicatorsByNameMap from "components/stocks/modern/OperatingSegments/genIndicatorsByNameMap";
import { genStandardizedCountries } from "components/stocks/modern/OperatingSegments/genStandardizedCountries";
import isOthersSegment from "components/stocks/modern/OperatingSegments/isOthersSegment";
import { useIndicatorType, usePlotType, useSelectedYear } from "components/stocks/modern/OperatingSegments/hooks";
import type { FinancialSegmentsFields } from "components/stocks/modern/OperatingSegments/types";
import AllSegmentsHistoryColumnsChart from "components/stocks/modern/chart/AllSegmentsHistoryColumnsChart";
import SegmentsPieChart from "components/stocks/modern/chart/SegmentsPieChart";
import classNames from "components/ui/classNames";
import getYear from "date-fns/getYear";
import parseISO from "date-fns/parseISO";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import { BarChartIcon, InfoIcon, PieChartIcon, UsersRoundIcon } from "lucide-react";
import { invariant } from "outvariant";
import { useEffect, useState } from "react";
import type { IntlShape } from "react-intl";
import { FormattedMessage, useIntl } from "react-intl";

interface OperatingSegmentsProps {
  kind: "GEOGRAPHIC" | "BUSINESS";
  tickerSymbols: string[];
  financialSegments: FinancialSegmentsFields;
  majorCustomers?: Array<{
    fiscalYear: number;
    flagText: string | null;
  }>;
}

export default function OperatingSegments({
  kind,
  tickerSymbols,
  financialSegments,
  majorCustomers = [],
}: OperatingSegmentsProps) {
  const intl = useIntl();
  const [plotType, setPlotType] = usePlotType();
  const [selectedYear, setSelectedYear] = useSelectedYear();
  const [indicatorType, setIndicatorType] = useIndicatorType();
  const [displayFormat, setDisplayFormat] = useState(DisplayFormat.Financial);

  const indicatorsMap = genIndicatorsByNameMap(intl);
  const indicatorsOrder = Object.keys(indicatorsMap);
  const filterOptions = calculateFilterOptions(financialSegments, indicatorsOrder);
  const allSegments = [...(financialSegments?.segments || [])];

  useEffect(() => {
    if (filterOptions.allYears.length > 0) {
      setSelectedYear(filterOptions.allYears[0]);
      setIndicatorType(filterOptions.indicatorsByYear[filterOptions.allYears[0]][0]);
    }
  }, [tickerSymbols]);

  if (filterOptions.allYears.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="space-y-2 text-center text-base tracking-wide text-gray-600">
          <div>
            <SadFaceIcon />
          </div>

          <p>
            <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
          </p>
        </div>
      </div>
    );
  }

  invariant(filterOptions.allYears[0], "No fiscal years available");
  invariant(filterOptions.indicatorsByYear[filterOptions.allYears[0]], "No indicators available");

  if (!indicatorType || !selectedYear || !filterOptions.indicatorsByYear[selectedYear]) {
    return null;
  }

  const indicatorDetails = indicatorsMap[indicatorType as FinancialSegmentsIndicatorName];
  const majorCustomer = majorCustomers.find(({ fiscalYear }) => fiscalYear === selectedYear);

  if (!indicatorDetails) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="space-y-2 text-center text-base tracking-wide text-gray-600">
          <div>
            <SadFaceIcon />
          </div>

          <p>
            <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
          </p>
        </div>
      </div>
    );
  }

  const segments = allSegments
    .map((node) => {
      const ratioIndicator = node.ratioAdjustedIndicators.find((node) => node.name === indicatorType);
      const financialIndicator = node.financialIndicators.find((node) => node.name === indicatorType);

      if (!ratioIndicator && !financialIndicator) {
        return null;
      }

      return {
        ...node,
        ratioAdjustedIndicators: node.ratioAdjustedIndicators.map((indicator) => {
          return {
            ...indicator,
            history: indicator.history.filter((item) => {
              return getYear(parseISO(item.periodEndingDate)) === selectedYear;
            }),
          };
        }),
        financialIndicators: node.financialIndicators.map((indicator) => {
          return {
            ...indicator,
            history: indicator.history.filter((item) => {
              return getYear(parseISO(item.periodEndingDate)) === selectedYear;
            }),
          };
        }),
        ratioIndicator: ratioIndicator
          ? {
              ...ratioIndicator,
              history: ratioIndicator.history.filter((item) => {
                return getYear(parseISO(item.periodEndingDate)) === selectedYear;
              }),
            }
          : null,
        financialIndicator: financialIndicator
          ? {
              ...financialIndicator,
              history: financialIndicator.history.filter((item) => {
                return getYear(parseISO(item.periodEndingDate)) === selectedYear;
              }),
            }
          : null,
      };
    })
    .filter(notUndefinedOrNull)
    .sort((a, b) => {
      return (b.financialIndicator?.value || 0) - (a.financialIndicator?.value || 0);
    });

  return (
    <OnlyPremiumRender>
      <div className="grid h-auto w-full grid-cols-1 items-start gap-4 lg:grid-cols-[max-content,1fr] lg:gap-8">
        <div className="flex h-full flex-col space-y-4 sm:w-96 sm:max-w-md">
          <div
            className={classNames("grid grid-cols-1 gap-4", "sm:items-center sm:gap-2", "lg:grid-cols-3", {
              "lg:grid-cols-2": kind === "GEOGRAPHIC",
            })}
          >
            <SingleValueSelect
              disabled={filterOptions.allYears.length <= 1 || plotType === "column"}
              values={filterOptions.allYears.map((node) => ({
                label: node.toString(),
                value: node.toString(),
                description: intl.formatList(
                  filterOptions.indicatorsByYear[node]
                    .map((indicator) => {
                      if (indicator !== "Operating Revenue") {
                        return null;
                      }

                      return indicatorsMap[indicator as FinancialSegmentsIndicatorName].abbreviation;
                    })
                    .filter(notUndefinedOrNull),
                  {
                    style: "long",
                  },
                ),
              }))}
              onChange={(value) => {
                const year = parseFloat(value);
                const availableIndicators = filterOptions.indicatorsByYear[year];

                setSelectedYear(year);

                if (!availableIndicators.includes(indicatorType)) {
                  setIndicatorType(availableIndicators[0]);
                }
              }}
              value={String(selectedYear)}
            />

            <PlotTypeSelector
              value={plotType}
              onChange={setPlotType}
              className="hidden w-full rounded-xl sm:w-full sm:max-w-sm lg:flex"
              itemClassName="h-8 w-full sm:w-full"
              types={[
                {
                  type: "pie",
                  label: intl.formatMessage({
                    id: "9RIsSS",
                    defaultMessage: "Pizza",
                    description: "Tipo de visualização do gráfico",
                  }),
                  icon: <PieChartIcon />,
                },
                {
                  type: "column",
                  label: intl.formatMessage({
                    id: "hZ073v",
                    defaultMessage: "Barra",
                    description: "Tipo de visualização do gráfico",
                  }),
                  icon: <BarChartIcon />,
                },
              ]}
            />

            {kind !== "GEOGRAPHIC" && (
              <DisplayFormatSelector
                value={displayFormat}
                onChange={setDisplayFormat}
                disabled={plotType !== "column"}
              />
            )}
          </div>

          <div className="h-full w-full space-y-4">
            <OperatingSegmentsList
              kind={kind}
              forYear={selectedYear}
              financialSegments={financialSegments}
              segments={segments}
            />
          </div>

          <PlotTypeSelector
            value={plotType}
            onChange={setPlotType}
            className="flex w-full rounded-xl sm:w-full sm:max-w-sm lg:hidden"
            itemClassName="h-8 w-full sm:w-full"
            types={[
              {
                type: "pie",
                label: intl.formatMessage({
                  id: "9RIsSS",
                  defaultMessage: "Pizza",
                  description: "Tipo de visualização do gráfico",
                }),
                icon: <PieChartIcon />,
              },
              {
                type: "column",
                label: intl.formatMessage({
                  id: "hZ073v",
                  defaultMessage: "Barra",
                  description: "Tipo de visualização do gráfico",
                }),
                icon: <BarChartIcon />,
              },
            ]}
          />
        </div>

        <div className="flex h-full items-center rounded-xl bg-slate-50/50 dark:bg-slate-900/50">
          {segments.length > 0 && plotType === "pie" && (
            <SegmentsPieChart
              className="h-80 min-h-80 w-full md:h-max lg:h-full"
              currency={financialSegments.currency}
              segments={getAllSegmentsForPieChart(kind, indicatorType, selectedYear, allSegments, intl)}
            />
          )}

          {segments.length > 0 && filterOptions.allYears.length > 0 && plotType === "column" && (
            <AllSegmentsHistoryColumnsChart
              className="h-80 min-h-80 w-full md:h-max lg:h-full"
              kind={kind}
              displayFormat={displayFormat}
              segments={getAllSegmentsForColumnChart(
                kind,
                displayFormat,
                indicatorType,
                selectedYear,
                allSegments,
                intl,
              )}
            />
          )}
        </div>

        {kind === "BUSINESS" && (financialSegments.revenueModelSummary || majorCustomer?.flagText) && (
          <section className="w-full space-y-2 lg:col-span-2">
            {majorCustomer?.flagText && (
              <div
                role="alert"
                className={classNames("space-y-2", {
                  "mb-4 border-b border-slate-200 pb-4 dark:border-slate-700":
                    financialSegments.revenueModelSummary,
                })}
              >
                <h2 className="flex items-center gap-2 text-lg font-bold text-orange-700 dark:text-orange-300">
                  <UsersRoundIcon aria-hidden={true} className="size-4 shrink-0" />
                  <FormattedMessage defaultMessage="Concentração de clientes" id="c0pSs6" />
                </h2>

                <p className="text-sm leading-7 text-slate-600 dark:text-slate-300">{majorCustomer.flagText}</p>
              </div>
            )}

            {financialSegments.revenueModelSummary && (
              <>
                <h2 className="flex items-center gap-2 text-lg font-bold text-slate-800 dark:text-slate-200">
                  <InfoIcon aria-hidden={true} className="size-4 shrink-0 text-slate-500 dark:text-slate-400" />
                  <FormattedMessage defaultMessage="Como Ganha Dinheiro" id="6h7QRc" />
                </h2>

                <p className="text-sm leading-7 text-slate-600 dark:text-slate-300">
                  {financialSegments.revenueModelSummary}
                </p>
              </>
            )}
          </section>
        )}
      </div>
    </OnlyPremiumRender>
  );
}

const getAllSegmentsForPieChart = (
  kind: "GEOGRAPHIC" | "BUSINESS",
  selectedIndicator: string,
  selectedYear: number,
  segments: FinancialSegmentsFields["segments"],
  intl: IntlShape,
  limit: number = kind === "GEOGRAPHIC" ? 5 : 10,
) => {
  const standardizedCountries = genStandardizedCountries(intl);
  const isGeographic = kind === "GEOGRAPHIC";

  const allSegments = segments
    .map((node) => {
      const financialIndicator = (node.financialIndicators || []).find(({ name }) => {
        return name === selectedIndicator;
      });
      const ratioIndicator = node.ratioAdjustedIndicators.find(({ name }) => {
        return name === selectedIndicator;
      });
      if (!ratioIndicator || !financialIndicator) {
        return null;
      }

      const financialIndicatorHistoryNode = financialIndicator.history.find(({ periodEndingDate }) => {
        return getYear(parseISO(periodEndingDate).getTime()) === selectedYear;
      });

      const ratioIndicatorHistoryNode = ratioIndicator.history.find((node) => {
        return getYear(parseISO(node.periodEndingDate).getTime()) === selectedYear;
      });
      if (!ratioIndicatorHistoryNode || !financialIndicatorHistoryNode) {
        return null;
      }

      const standardizedCountry = standardizedCountries.find((candidate) => {
        return candidate.standardizedName === node.name;
      });

      return {
        name: isGeographic && standardizedCountry ? standardizedCountry.name : node.name,
        value: ratioIndicatorHistoryNode.value,
        financialValue: financialIndicatorHistoryNode.value,
      };
    })
    .filter(notUndefinedOrNull)
    .sort((a, b) => b.value - a.value);

  if (!isGeographic) {
    return allSegments.slice(0, limit);
  }

  const namedSegments = allSegments.filter((node) => !isOthersSegment(node));
  const mostRelevantSegments = namedSegments.slice(0, limit);
  const restSegments = [...namedSegments.slice(limit), ...allSegments.filter(isOthersSegment)];

  if (restSegments.length === 0) {
    return mostRelevantSegments;
  }

  return [
    ...mostRelevantSegments,
    restSegments.reduce(
      (acc, curr) => {
        return {
          ...acc,
          value: acc.value + curr.value,
          financialValue: acc.financialValue + curr.financialValue,
        };
      },
      {
        name: intl.formatMessage({
          id: "8K4Qok",
          defaultMessage: "Outros",
          description: "Complemento do gráfico de receitas",
        }),
        value: 0,
        financialValue: 0,
      },
    ),
  ];
};

const getAllSegmentsForColumnChart = (
  kind: "GEOGRAPHIC" | "BUSINESS",
  displayFormat: DisplayFormat,
  selectedIndicator: string,
  selectedYear: number,
  segments: FinancialSegmentsFields["segments"],
  intl: IntlShape,
) => {
  const standardizedCountries = genStandardizedCountries(intl);
  const isGeographic = kind === "GEOGRAPHIC";
  const isRatioDisplayFormat = displayFormat === DisplayFormat.Percent;

  if (isGeographic || isRatioDisplayFormat) {
    const allSegments = segments
      .map((node) => {
        const ratioIndicator = node.ratioAdjustedIndicators.find(({ name }) => {
          return name === selectedIndicator;
        });
        if (!ratioIndicator) {
          return null;
        }
        const standardizedCountry = standardizedCountries.find((candidate) => {
          return candidate.standardizedName === node.name;
        });
        const values = ratioIndicator.history
          .map((point) => ({
            x: getYear(parseISO(point.periodEndingDate).getTime()),
            y: point.value,
          }))
          .sort((a, b) => b.x - a.x);
        return {
          name: standardizedCountry ? standardizedCountry.name : node.name,
          yearValue: values.find((point) => point.x === selectedYear)?.y || 0,
          values,
        };
      })
      .filter(notUndefinedOrNull)
      .sort((a, b) => b.yearValue - a.yearValue);

    if (allSegments.length <= 5) {
      return allSegments.map((node) => {
        if (isOthersSegment(node)) {
          return {
            ...node,
            name: intl.formatMessage({
              id: "8K4Qok",
              defaultMessage: "Outros",
              description: "Complemento do gráfico de receitas",
            }),
          };
        }

        return node;
      });
    }

    const otherSegments = allSegments.filter(isOthersSegment);
    const filteredSegments = allSegments.filter((node) => !isOthersSegment(node));

    const mostRelevantSegments = filteredSegments.slice(0, 5);
    const restSegments = [...filteredSegments.slice(5, allSegments.length), ...otherSegments];

    return [
      ...mostRelevantSegments,
      restSegments.reduce(
        (acc, curr) => {
          const { values } = curr;

          return {
            name: intl.formatMessage({
              id: "8K4Qok",
              defaultMessage: "Outros",
              description: "Complemento do gráfico de receitas",
            }),
            values: acc.values.map((point) => {
              const history = values.find((node) => node.x === point.x);

              return {
                x: point.x,
                y: point.y + (history ? history.y : 0),
              };
            }),
          };
        },
        {
          name: "",
          values: allSegments
            .sort((a, b) => (a.values.length > b.values.length ? -1 : 1))[0]
            .values.map((point) => ({
              x: point.x,
              y: 0,
            })),
        },
      ),
    ];
  }

  return segments
    .map((node) => {
      const financialIndicator = node.financialIndicators.find(({ name }) => {
        return name === selectedIndicator;
      });
      if (!financialIndicator) {
        return null;
      }

      const values = financialIndicator.history
        .map((point) => ({
          x: getYear(parseISO(point.periodEndingDate).getTime()),
          y: point.value,
        }))
        .sort((a, b) => b.x - a.x);
      return {
        name: node.name,
        maxValue: Math.max(...values.map((point) => point.y)),
        values,
      };
    })
    .filter(notUndefinedOrNull)
    .sort((a, b) => b.maxValue - a.maxValue);
};
